import IconBolt from 'assets/icons/bolt.svg';
import IconExpandMore from 'assets/icons/expand-more.svg';
import cn from 'classnames';
import { m } from 'motion/react';
import { gtm, selectElement, unselectElement } from 'tracking';

interface QAItemProps {
  activeIndex: number | null;
  answer: string;
  index: number;
  question: string;
  trackingName: string;
  setActiveIndex: (index: number | null) => void;
}

/**
 * QAItem component that displays a question with expandable answer
 * for the AI bot question-answer section.
 */
const QAItem: React.FC<QAItemProps> = ({
  activeIndex,
  answer,
  index,
  question,
  trackingName,
  setActiveIndex,
}) => {
  const isExpanded = activeIndex === index;

  const toggleExpand = (): void => {
    if (isExpanded) {
      gtm(
        unselectElement({
          element: [trackingName, `Open Question ${index + 1}`],
        })
      );
    } else {
      gtm(
        selectElement({
          element: [trackingName, `Open Question ${index + 1}`],
        })
      );
    }

    setActiveIndex(isExpanded ? null : index);
  };

  return (
    <div className="bg-background-default flex gap-2 rounded-lg py-2 pr-4 pl-2.5">
      <IconBolt className="text-secondary-main h-6 w-6 shrink-0" />
      <div className="flex w-full flex-col">
        <button
          className="text-text-light-bg-primary flex w-full items-center justify-between gap-2 text-left outline-hidden"
          onClick={toggleExpand}
          data-test={`aibot-preset-question-${index}`}
        >
          <span>{question}</span>
          <m.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.35 }}
          >
            <IconExpandMore className="text-primary-main h-6 w-6 shrink-0" />
          </m.div>
        </button>
        <m.div
          className={cn('overflow-hidden')}
          initial={{ opacity: 0, height: 0 }}
          animate={
            isExpanded
              ? { opacity: 1, height: 'auto' }
              : { opacity: 0, height: 0 }
          }
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          data-test={`aibot-answer-content-${index}`}
        >
          <div className="mt-0.5">{answer}</div>
        </m.div>
      </div>
    </div>
  );
};

export default QAItem;
